import { useId, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { formatTime } from "../utils/timeFormatter";

const STORAGE_KEY = "testSettings";

const getSaved = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved ? JSON.parse(saved) : {};
};

const TestSetting = () => {
  const saved = getSaved();

  const [wordNums, setWordNums] = useState<number>(saved.wordNums ?? 1000);
  const [seconds, setSeconds] = useState<number>(saved.seconds ?? 60);
  const [highlightMistakes, setHighlightMistakes] = useState<boolean>(saved.highlightMistakes ?? true);
  const [hideTimer, setHideTimer] = useState<boolean>(saved.hideTimer ?? false);
  const [disableBackspace, setDisableBackspace] = useState<boolean>(saved.disableBackspace ?? false);
  const [highlightCurrentWord, setHighlightCurrentWord] = useState<boolean>(saved.highlightCurrentWord ?? true);
  const [clearInput, setClearInput] = useState<boolean>(saved.clearInput ?? true);

  const navigate = useNavigate();

  // Save whenever settings change
  useEffect(() => {
    const settings = { wordNums, seconds, highlightMistakes, hideTimer, disableBackspace, highlightCurrentWord, clearInput };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [wordNums, seconds, highlightMistakes, hideTimer, disableBackspace, highlightCurrentWord, clearInput]);

  const handleStart = () => {
    navigate("/test", { state: { wordNums, seconds, highlightCurrentWord, highlightMistakes, clearInput, hideTimer, disableBackspace } });
  };

  const endLess = () => {
    navigate("/endless");
  };

  return (
    <div className="max-w-md px-5 mx-auto space-y-6">
      {/* Time slider */}
      <div className="flex flex-col">
        <div className="flex justify-between items-center mb-2">
          <label className="text-gray-800 dark:text-gray-400 font-medium text-sm sm:text-sm md:text-base">Time: </label>
          <span className="text-primary dark:text-white font-semibold rounded text-sm sm:text-sm md:text-base">{formatTime(seconds)}</span>
        </div>
        <input type="range"
          min={60}
          max={1800}
          step={30}
          value={seconds}
          onChange={(e) => setSeconds(Number(e.target.value))}
          className="w-full h-2 bg-gray-200 dark:bg-gray-800 rounded-lg appearance-none cursor-pointer accent-primary"
        />
      </div>

      {/* Checkboxes */}
      <div className="mb-3 flex flex-row flex-wrap gap-6">
        <Checkbox labelText="Highlight Mistakes" value={highlightMistakes} onChange={() => setHighlightMistakes(s => !s)} />
        <Checkbox labelText="Highlight Current Word" value={highlightCurrentWord} onChange={() => setHighlightCurrentWord(s => !s)} />
        <Checkbox labelText="Clear input after entered" value={clearInput} onChange={() => setClearInput(s => !s)} />
        <Checkbox labelText="Hide Timer" value={hideTimer} onChange={() => setHideTimer(s => !s)} />
        <Checkbox labelText="Disable Backspace" value={disableBackspace} onChange={() => setDisableBackspace(s => !s)} />
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-1 justify-center items-center">
        <TestButton onClick={handleStart}/>
        {/* <button onClick={handleStart} className="text-nowrap text-sm sm:text-sm md:text-base px-12 py-3 rounded-lg bg-primary hover:bg-blue-900 hover:shadow active:scale-95 border-t border-t-blue-500 border-b border-b-blue-500 text-gray-50">Start Test</button> */}
        <span>or</span>
        <button onClick={endLess} className="bg-gray-200 dark:bg-gray-950 shadow text-nowrap hover:shadow text-sm sm:text-sm md:text-base py-2 px-4 rounded-lg text-gray-800 dark:text-gray-400 dark:border dark:border-gray-900">
          Begin Endless Practice
        </button>
      </div>
    </div>
  );
};

interface CheckboxProps {
  labelText: string;
  value: boolean;
  onChange: () => void;
}

export const Checkbox: React.FC<CheckboxProps> = ({ labelText, value, onChange }) => {
  const id = useId();
  return (
    <label htmlFor={id} className="cursor-pointer flex flex-row items-center gap-2.5 dark:text-white light:text-black">
      <input checked={value} onChange={onChange} id={id} type="checkbox" className="peer hidden" />
      <CheckboxSvg />
      <span className="text-gray-800 dark:text-gray-400 font-medium text-sm sm:text-sm md:text-base">{labelText}</span>
    </label>
  );
};

const CheckboxSvg = () => {
  return (
    <div className="h-5 w-5 flex rounded-md border border-[#a2a1a833] light:bg-[#e8e8e8] dark:bg-[#212121] peer-checked:bg-[#7152f3] transition">
      <svg fill="none" viewBox="0 0 24 24" className="w-5 h-5 light:stroke-[#212121] dark:stroke-[#212121]" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 12.6111L8.92308 17.5L20 6.5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
      </svg>
    </div>
  );
};

export default TestSetting;


const TestButton = ({ onClick }: any) => {
  return <button onClick={onClick}
    className="relative group text-slate-950 transition-all flex items-center justify-center whitespace-nowrap rounded-lg hover:rotate-[3deg] will-change-transform duration-300 h-12  text-sm sm:text-sm md:text-base pl-[5rem] pr-6 bg-yellow-400"
  >
    <div
      className="absolute left-0 top-0 mt-1 ml-1 bg-white text-slate-950 p-[0.35rem] bottom-1 group-hover:w-[calc(100%-0.5rem)] transition-all rounded-md duration-300 h-10 w-12"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        className="h-full w-full"
      >
        <path d="M10 8h.01"></path>
        <path d="M12 12h.01"></path>
        <path d="M14 8h.01"></path>
        <path d="M16 12h.01"></path>
        <path d="M18 8h.01"></path>
        <path d="M6 8h.01"></path>
        <path d="M7 16h10"></path>
        <path d="M8 12h.01"></path>
        <rect width="20" height="16" x="2" y="4" rx="2"></rect>
      </svg>
    </div>

    <div>Start Test</div>

    <div
      className="bg-orange-400 absolute flex rounded-full animate-ping opacity-75 h-5 w-5 -top-2 -right-2"
    ></div>
    <div
      className="bg-orange-600 absolute flex rounded-full scale-[90%] h-5 w-5 -top-2 -right-2"
    ></div>
  </button>
}